import {
  Sun,
  Moon,
  Plus,
  FileText,
  BarChart3,
  Factory,
  Users,
  Shield,
  Leaf,
  Target,
  Briefcase,
  FolderKanban,
  Database,
  CircleDot,
  UserCheck,
  Link2,
  Sparkles,
  BookOpen,
  Settings,
  LogOut,
  Copy,
  Upload,
  Clock,
  Map,
} from "lucide-react";
import type { ThemeMode } from "@/stores/theme";
import type { CmdItem } from "./types";
import { toast } from "sonner";
import { isSupplierRole } from "@/lib/workspaceAccess";

const SUPPLIER_COMMANDS = new Set([
  "p-customers",
  "p-resource",
  "s-org-info",
  "s-security",
  "s-sub",
  "a-theme",
  "a-copy",
  "a-signout",
]);

interface BuildItemsDeps {
  go: (path: string) => void;
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleAIPanel: () => void;
  setOpen: (open: boolean) => void;
  logout: () => Promise<boolean>;
  aiEnabled: boolean;
  role?: number;
}

export function buildAllItems(deps: BuildItemsDeps): CmdItem[] {
  const { go, theme, setTheme, toggleAIPanel, setOpen, logout, aiEnabled } =
    deps;
  const nextTheme = theme === "light" ? "dark" : "light";
  const nextThemeLabel = nextTheme === "dark" ? "Dark" : "Light";
  const ThemeIcon = theme === "dark" ? Sun : Moon;

  return [
    // ── Pages ──
    {
      id: "p-home",
      label: "Home",
      icon: BarChart3,
      category: "Pages",
      keywords: ["home", "landing", "start"],
      onSelect: () => go("/home"),
    },
    {
      id: "p-dash",
      label: "Dashboard",
      icon: BarChart3,
      category: "Pages",
      keywords: ["dashboard", "analytics", "charts"],
      onSelect: () => go("/dashboard"),
    },
    {
      id: "p-reports",
      label: "Reports",
      icon: FileText,
      category: "Pages",
      keywords: ["reports", "create report", "generate"],
      onSelect: () => go("/reports"),
    },
    {
      id: "p-emissions",
      label: "Emissions",
      icon: Leaf,
      category: "Pages",
      keywords: ["emissions", "log", "ghg", "scope"],
      onSelect: () => go("/log/emissions"),
    },
    {
      id: "p-targets",
      label: "Targets",
      icon: Target,
      category: "Pages",
      keywords: ["targets", "goals", "reduction"],
      onSelect: () => go("/targets"),
    },
    {
      id: "p-assets",
      label: "Assets",
      icon: Briefcase,
      category: "Pages",
      keywords: ["assets", "infrastructure"],
      onSelect: () => go("/assets"),
    },
    {
      id: "p-offsets",
      label: "Offsets",
      icon: FolderKanban,
      category: "Pages",
      keywords: ["offsets", "carbon credits"],
      onSelect: () => go("/offsets"),
    },
    {
      id: "p-facilities",
      label: "Facilities",
      icon: Factory,
      category: "Pages",
      keywords: ["facilities", "sites", "locations", "buildings"],
      onSelect: () => go("/organisation/facilities"),
    },
    {
      id: "p-employees",
      label: "Employees",
      icon: Users,
      category: "Pages",
      keywords: ["employees", "team", "people"],
      onSelect: () => go("/organisation/employees"),
    },
    {
      id: "p-disclosures",
      label: "Disclosures",
      icon: FileText,
      category: "Pages",
      keywords: ["disclosures", "gri", "tcfd", "ifrs", "brsr", "compliance"],
      onSelect: () => go("/organisation/disclosures"),
    },
    {
      id: "p-audits",
      label: "Audits",
      icon: Shield,
      category: "Pages",
      keywords: ["audits", "verification", "review"],
      onSelect: () => go("/audits"),
    },
    {
      id: "p-std-factors",
      label: "Standard Emission Factors",
      icon: CircleDot,
      category: "Pages",
      keywords: ["standard", "emission factors", "ghg protocol"],
      onSelect: () => go("/datasource/standardemissionfactor"),
    },
    {
      id: "p-cust-factors",
      label: "Custom Emission Factors",
      icon: Database,
      category: "Pages",
      keywords: ["custom", "emission factors", "user defined"],
      onSelect: () => go("/datasource/customemissionfactor"),
    },
    {
      id: "p-suppliers",
      label: "Suppliers",
      icon: UserCheck,
      category: "Pages",
      keywords: ["suppliers", "supply chain", "srm"],
      onSelect: () => go("/partners/suppliers/dashboard"),
    },
    {
      id: "p-customers",
      label: "Customers",
      icon: Link2,
      category: "Pages",
      keywords: ["customers", "clients"],
      onSelect: () => go("/partners/customers"),
    },
    {
      id: "p-catmap",
      label: "Category Mapping",
      icon: Map,
      category: "Pages",
      keywords: ["category", "mapping", "classify"],
      onSelect: () => go("/cat-map"),
    },
    {
      id: "p-bulk",
      label: "Add emissions",
      icon: Upload,
      category: "Pages",
      keywords: ["bulk", "upload", "import", "csv", "excel"],
      onSelect: () => go("/log/upload"),
    },
    {
      // Not AI-gated on purpose: in-flight uploads must stay reviewable after
      // an admin switches the assistant off.
      id: "p-upload-activity",
      label: "Upload Activity",
      icon: Clock,
      category: "Pages",
      keywords: ["upload", "review", "pending", "activity", "extraction"],
      onSelect: () => go("/emissions/upload-activity"),
    },
    {
      id: "p-resource",
      label: "Resource Centre",
      icon: BookOpen,
      category: "Pages",
      keywords: ["resources", "guides", "docs", "help"],
      onSelect: () => go("/resourcecentre"),
    },
    {
      id: "p-chat",
      label: "Green Mirror",
      icon: Sparkles,
      category: "Pages",
      keywords: ["ai", "assistant", "chat", "copilot"],
      onSelect: () => go("/chat"),
    },

    // ── Create / Quick actions ──
    {
      id: "a-new-report",
      label: "Create Report",
      icon: Plus,
      category: "Create",
      keywords: ["create", "new", "report", "generate"],
      onSelect: () => go("/reports"),
    },
    {
      id: "a-new-facility",
      label: "Add Facility",
      icon: Plus,
      category: "Create",
      keywords: ["add", "new", "facility", "site"],
      onSelect: () => go("/organisation/facilities/create"),
    },
    {
      id: "a-new-employee",
      label: "Add Employee",
      icon: Plus,
      category: "Create",
      keywords: ["add", "new", "employee", "team member"],
      onSelect: () => go("/organisation/employees"),
    },
    {
      id: "a-new-factor",
      label: "Create Custom Factor",
      icon: Plus,
      category: "Create",
      keywords: ["create", "new", "emission factor", "custom"],
      onSelect: () => go("/datasource/customemissionfactor"),
    },

    // ── Settings ──
    {
      id: "s-org-info",
      label: "Organisation Information",
      icon: Settings,
      category: "Settings",
      keywords: ["organisation", "info", "company", "details"],
      onSelect: () => go("/settings/organisationInformation"),
    },
    {
      id: "s-prefs",
      label: "Preferences",
      icon: Settings,
      category: "Settings",
      keywords: ["preferences", "defaults", "configuration"],
      onSelect: () => go("/settings/organisationpreference"),
    },
    {
      id: "s-security",
      label: "Security",
      icon: Shield,
      category: "Settings",
      keywords: ["security", "password", "2fa", "mfa"],
      onSelect: () => go("/settings/security"),
    },
    {
      id: "s-rates",
      label: "Exchange Rates",
      icon: Settings,
      category: "Settings",
      keywords: ["exchange", "rates", "currency"],
      onSelect: () => go("/settings/exchangerates"),
    },
    {
      id: "s-sub",
      label: "Subscription",
      icon: Settings,
      category: "Settings",
      keywords: ["subscription", "plan", "billing"],
      onSelect: () => go("/settings/subscription"),
    },

    // ── Account / Actions ──
    {
      id: "a-theme",
      label: `Switch to ${nextThemeLabel}`,
      icon: ThemeIcon,
      category: "Account",
      keywords: ["theme", "dark", "light", "mode", "appearance"],
      onSelect: () => {
        setTheme(nextTheme);
        setOpen(false);
      },
    },
    {
      id: "a-ai",
      label: "Toggle AI Panel",
      icon: Sparkles,
      category: "Account",
      keywords: ["ai", "panel", "chat", "toggle"],
      onSelect: () => {
        toggleAIPanel();
        setOpen(false);
      },
    },
    {
      id: "a-copy",
      label: "Copy Current URL",
      icon: Copy,
      category: "Account",
      keywords: ["copy", "url", "link", "share"],
      onSelect: () => {
        navigator.clipboard?.writeText(window.location.href);
        setOpen(false);
      },
    },
    {
      id: "a-signout",
      label: "Sign Out",
      icon: LogOut,
      category: "Account",
      keywords: ["sign", "out", "logout", "exit"],
      onSelect: async () => {
        if (await logout()) {
          setOpen(false);
        } else {
          toast.error("Couldn't sign out. Please try again.");
        }
      },
    },
  ].filter(
    (item) =>
      (!isSupplierRole(deps.role) || SUPPLIER_COMMANDS.has(item.id)) &&
      (aiEnabled ||
        (item.id !== "p-chat" && item.id !== "a-ai" && item.id !== "p-bulk")),
  );
}
